import React, { useState } from "react";
import Plot from "react-plotly.js";

import { AddFilterBaseFunc, chartAttrs, defaultConfig, defaultStyles, graphColors, hierarchyFilterFactory, IFilterKeys, IFilters, RemoveFilterBaseFunc } from "models/core";
import { DashboardFilterKeys } from "models/mdt";
import { ReviewsByPeriodHierarchy, PatientsByPeriodHierarchy } from "models/mdt/plotdata";
import { PlotData, PlotType } from "plotly.js";
import { Dialog, Grid } from "@mui/material";
import { addHierarchyFilterFactory } from "helpers/filterHelper";
import { HierarchyBreadcrumb } from "components/common/HierarchyBreadcrumb";
import { DashTable } from "components/common/DashTable";
import { DashChart } from "components/common/DashChart";
import { ChartIcons } from "components/common/ChartIcons";
import { chartDimesionsCalc } from "../core";
import watermark from "../../static/qca-black.png";
import { CreateChart } from "components/common/CreateChart";

interface IProps {
	plotDataPatients: PatientsByPeriodHierarchy.IHierarchyPlotData;
    activeFilters: IFilters<IFilterKeys>;
	//addFilterFn: (e: any, filterKey: string, getY: (p) => string) => void;
	addFilterFn: AddFilterBaseFunc<typeof DashboardFilterKeys>;
    removeFilterFn: RemoveFilterBaseFunc<typeof DashboardFilterKeys>;
}

/**
 * Reviews by service chart / My MDTs
 */
export function NoMDTPatientsTrendChart(props: IProps) {
	const { plotDataPatients, activeFilters, addFilterFn, removeFilterFn } = props;
	const hierarchy = plotDataPatients;

	let valuesPOriginal = [];
	let labelsPOriginal =[];

	let valuesPReversed = [];
	let labelsPReversed =[];

	let Patients = {};

	//If a diagnosis year is selected, trim the values to only show after that year 
	if (activeFilters.DiagYear?.value && !activeFilters.Year?.value && Number(activeFilters.DiagYear.value.sort()[0]) >= 2010) {

		const labelIndexP = hierarchy.labels.findIndex(a => a == activeFilters.DiagYear.value.sort()[0]);

		valuesPOriginal = hierarchy.values.slice(labelIndexP);
		labelsPOriginal = hierarchy.labels.slice(labelIndexP);
		valuesPReversed = hierarchy.values.slice(labelIndexP).slice().reverse();
		labelsPReversed = hierarchy.labels.slice(labelIndexP).slice().reverse();
		Patients = {"name": "Patients", "labels": labelsPOriginal, "labelsReversed": labelsPReversed, "values": valuesPOriginal, "valuesReversed": valuesPReversed};

	} else {
	
		valuesPOriginal = hierarchy.values;
		labelsPOriginal = hierarchy.labels;
		valuesPReversed = hierarchy.values.slice().reverse();
		labelsPReversed = hierarchy.labels.slice().reverse();
		Patients = {"name": "Patients", "labels": labelsPOriginal, "labelsReversed": labelsPReversed, "values": valuesPOriginal, "valuesReversed": valuesPReversed};
	}

	const tracesArray: any[] = [];
	tracesArray.push(Patients);

    const [ chartType, setChartType ] = useState("scatter");
    const [ chartOrientation, setChartOrientation ] = useState("v");
    const [ fullScreenOpen, setFullScreenState ] = useState(false);
	
	const showChart = valuesPOriginal.length > 0;
	const filterKey = DashboardFilterKeys.Year.key;
	const filterFn = addHierarchyFilterFactory(addFilterFn, filterKey, hierarchy, (chartOrientation =="h" ? p => p.y.toString() : p => p.x.toString()));
	const chartDimensions = chartDimesionsCalc(50, 12, 4);
	const textFn = String;

	const xtitle = (chartOrientation == "v" ? "Admission Date" : "Number of Patients");
	const ytitle = (chartOrientation =="v" ? "Number of Patients" : "Admission Date");
	const chartTitle = "Patients treated at my facility with no evidence of MDT presentation";

	const traces: Partial<PlotData>[] = [];

		tracesArray?.forEach((a, i) => {
			switch (true) {
				case chartOrientation == "v":
					traces.push({
						x: a.labels,
						y: a.values,
						name: a.name,
						text: a.values.map(textFn),
						textposition: (chartType == "bar" ? "outside" : "none"),
						textfont: {
							size: 12,
						  },
						hovertext: a.labels,
						hovertemplate: "%{hovertext}<br>" +
									"Patients: %{y:,}<br>" +
									"<extra></extra>" + 
									"<br>" +
									"To view specific year(s):<br>" +
									"Click and drag on years to select",
						type: chartType,
						orientation: chartOrientation,
						marker: {},
						showlegend: false,
					} as unknown as Plotly.PlotData)
				break;

				case chartOrientation == "h":
					traces.push({
						x: a.valuesReversed,
						y: a.labelsReversed,
						name: a.name, 
						text: a.valuesReversed.map(textFn),
						textposition: (chartType == "bar" ? "outside" : "none"),
						textfont: {
							size: 12,
						  },
						hovertext: a.labelsReversed,
						hovertemplate: "%{hovertext}<br>" +
									"Patients: %{x:,}<br>" +
									"<extra></extra>" + 
									"<br>" +
									"To view specific year(s):<br>" +
									"Click and drag on years to select",
						type: chartType,
						orientation: chartOrientation,
						marker: {},
						showlegend: false,
					} as unknown as Plotly.PlotData)
				break;

		}
		});

	const toolTipText = ["Presentations:", "A count of presentations to MDT where a patient can present multiple times", 
							"Patients:", "A unique count of patients presented to MDT", 
							"Re-presentation rate:", "The rate at which a patient presents more than once"];


	const logo = watermark;
	const  fullScreenChart =
		<Dialog
			open={fullScreenOpen || false}
			fullWidth={true}
			maxWidth={false}
			style={{width: (window.innerWidth - 100), 
					height: ((chartDimensions.fullScreenPaperHeight*1.3) + "px"),
					marginLeft: 50,
					marginRight: 50 }}
		>
				<DashChart style={{height: (chartDimensions.fullScreenPaperHeight) + "px"}} 
							heading ={chartTitle} 
							chartKey = {chartTitle+"fullScreen"}
						> 
					<div style={{marginTop: "-70px", float: "right", display: "inline"}}>
					<ChartIcons vbar ={true} hbar ={true}
						table={true}
						scatter={true}
						pie={false}
						downloadAsCsv={true}
						downloadAsPng={true}
						isFullScreen={fullScreenOpen}
						viewAsFullScreen={true}
						tooltipInfo={true}
						tooltipText={toolTipText}
						chartOrientation={chartOrientation}
						chartType={chartType}
						traces = {traces}
						xLabel = {(chartOrientation=="h" ? ytitle : xtitle)} 
						yLabel = {(chartOrientation=="h" ? xtitle : ytitle)}
						title= {chartTitle}
						setChartOrientation = {setChartOrientation}
						setChartType = {setChartType}
						setFullScreen = {setFullScreenState}
						currentFilters = {activeFilters}
						/>
					<br/>
					{<img
					src={logo}
					alt="Cancer Alliance Queensland"
					width={200}
					style={{marginTop: "20px"}}
						/> }
					</div> 
					{CreateChart(chartDimensions.fullScreenChartHeight,
								 chartDimensions.fullScreenChartWidth,
								 chartDimensions.fullScreenPaperHeight,
								 chartOrientation,
								 chartType, 
								 showChart, 
								 xtitle, 
								 ytitle, 
								 labelsPOriginal,
								 valuesPOriginal, 
								 traces, 
								 filterFn,
								 )}
				</DashChart>
		</Dialog>

	const breadcrumb = (
		<HierarchyBreadcrumb data={hierarchy} filterKey={filterKey} addFilter={addFilterFn} removeFilter={removeFilterFn} />
		);
	
	return (
		<div className={"paper"} key={chartTitle}
        style={{
            float: "right",
            width: "100%",
			padding: "12px",
			maxHeight: "450px"
        }}
        >  
		<ChartIcons vbar ={true} hbar ={true}
			table={true}
			scatter={true}
			pie={false}
			downloadAsCsv={true}
			downloadAsPng={true}
			isFullScreen={fullScreenOpen}
			viewAsFullScreen={true}
			tooltipInfo={true}
			tooltipText={toolTipText}
			chartOrientation={chartOrientation}
			chartType={chartType}
			traces = {traces}
			xLabel = {(chartOrientation=="h" ? ytitle : xtitle)} 
			yLabel = {(chartOrientation=="h" ? xtitle : ytitle)}
			title= {chartTitle}
            setChartOrientation = {setChartOrientation}
            setChartType = {setChartType}
			setFullScreen = {setFullScreenState}
            currentFilters = {activeFilters}
             />
             
		<DashChart chartKey={chartTitle+"chart"} heading ={chartTitle} breadcrumb={breadcrumb} style ={{height: chartDimensions.paperHeight + "px", 
																										/*width: chartDimensions.paperWidth*/}}>
			{CreateChart(chartDimensions.chartHeight,
								 chartDimensions.chartWidth,
								 chartDimensions.paperHeight,
								 chartOrientation,
								 chartType, 
								 showChart, 
								 xtitle, 
								 ytitle, 
								 labelsPOriginal,
								 valuesPOriginal, 
								 traces, 
								 filterFn,
								 )} 
            {fullScreenChart}
		</DashChart>
		</div>
	);
}
